import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import DictionaryService from '../../../services/dictionary.service';
import { localizeToolkitContent } from '../../../utils/toolkit-content-localization.util';

@Component({
	standalone: true,
	selector: 'toolkit-survey-results',
	templateUrl: './survey-results.component.html',
	imports: [CommonModule]
})
export class ToolkitSurveyResultsComponent implements OnChanges {
	private dictionaryService: DictionaryService = inject(DictionaryService);

	@Input() assessment: any = null;
	@Input() questions: any = null;
	@Input() scoring: any = null;
	@Input() assignment: any = null;
	@Input() answers: any[] = [];
	@Input() audience: 'client' | 'therapist' = 'client';

	vm: any = {
		title: '',
		subtitle: '',
		completedDate: '',
		score: 0,
		maxScore: 0,
		scoreLabel: '',
		band: null,
		feedbackText: '',
		riskTriggered: false,
		riskMessage: '',
		subscales: [],
		items: []
	};

	ngOnChanges(_changes: SimpleChanges) {
		this.rebuild();
	}

	scoreBandLabel(label: string): string {
		return this.bandTranslation(label) || label || '';
	}

	private rebuild() {
		const instrument = this.instrument();
		const scoring = this.localize(this.scoring || {});
		const items = this.sortedItems(instrument);
		const answers = Array.isArray(this.answers) ? this.answers : [];
		const method = (scoring?.scoringMethod || 'sum').toString();
		const adjusted = items.map((item, index) => this.adjustedValue(item, answers[index], instrument));
		const subscales = this.computeSubscales(scoring, items, adjusted);
		const score = this.computeScore(method, adjusted, subscales);
		const maxScore = Number(scoring?.maxScore ?? this.defaultMaxScore(items, instrument));
		const band = this.localizedBand(this.findBand(scoring?.bands, score));
		const risk = this.computeRisk(scoring?.riskRules, items, answers);

		this.vm = {
			copy: this.copy(),
			title: this.localizedText(instrument, 'title') || this.localizedText(this.assessment, 'title') || instrument?.code || '',
			subtitle: this.localizedText(instrument, 'fullTitle') || this.localizedText(instrument, 'description') || this.localizedText(this.assessment, 'description'),
			completedDate: this.formatDate(this.assignment?.completedAt || this.assignment?.updatedAt || this.assignment?.modified),
			score,
			maxScore,
			scoreLabel: this.formatScore(score),
			band,
			feedbackText: this.feedbackText(band),
			riskTriggered: risk.triggered,
			riskMessage: risk.message,
			subscales,
			items: items.map((item, index) => ({
				order: item?.order || index + 1,
				text: this.localizedText(item, 'text'),
				rawValue: answers[index],
				value: adjusted[index],
				response: this.responseLabel(instrument, answers[index]),
				reversed: !!item?.reverse_scored,
				risk: !!item?.risk_item || risk.itemId === item?.id
			}))
		};
	}

	private instrument(): any {
		const raw = this.questions?.instrument && typeof this.questions.instrument === 'object'
			? this.questions.instrument
			: this.questions || {};
		return this.localize(raw);
	}

	private sortedItems(instrument: any): any[] {
		const items = Array.isArray(instrument?.items) ? instrument.items : [];
		return items.slice().sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0));
	}

	private adjustedValue(item: any, raw: any, instrument: any): number {
		const value = Number(raw);
		if (!isFinite(value)) return 0;
		if (!item?.reverse_scored) return value;
		const values = this.scaleValues(instrument);
		if (!values.length) return value;
		return Math.max(...values) + Math.min(...values) - value;
	}

	private scaleValues(instrument: any): number[] {
		const scale = Array.isArray(instrument?.responseScale) ? instrument.responseScale : [];
		return scale
			.map((option) => Number(option?.value))
			.filter((value) => isFinite(value));
	}

	private defaultMaxScore(items: any[], instrument: any): number {
		const values = this.scaleValues(instrument);
		const max = values.length ? Math.max(...values) : 0;
		return items.length * max;
	}

	private computeSubscales(scoring: any, items: any[], adjusted: number[]): any[] {
		const subscales = Array.isArray(scoring?.subscales) ? scoring.subscales : [];
		return subscales.map((subscale) => {
			const orders = this.subscaleOrders(subscale);
			const values = items
				.map((item, index) => ({ item, value: adjusted[index] }))
				.filter((entry) => orders.includes(Number(entry.item?.order)) || (subscale?.key && entry.item?.subscale_key === subscale.key))
				.map((entry) => entry.value);
			const sum = values.reduce((total, value) => total + value, 0);
			const score = values.length ? sum / values.length : 0;
			const band = this.findBand(scoring?.bands, score);
			return {
				key: subscale?.key || '',
				label: this.localizedText(subscale, 'label') || subscale?.key || '',
				score,
				scoreLabel: this.formatScore(score),
				maxScore: Number(subscale?.maxScore ?? scoring?.maxScore ?? 0),
				band: this.localizedBand(band)
			};
		});
	}

	private subscaleOrders(subscale: any): number[] {
		if (Array.isArray(subscale?.items)) {
			return subscale.items.map((item) => Number(item)).filter((value) => isFinite(value));
		}
		if (Array.isArray(subscale?.itemRange) && subscale.itemRange.length === 2) {
			const start = Number(subscale.itemRange[0]);
			const end = Number(subscale.itemRange[1]);
			if (isFinite(start) && isFinite(end) && end >= start) {
				const out: number[] = [];
				for (let i = start; i <= end; i++) out.push(i);
				return out;
			}
		}
		return [];
	}

	private computeScore(method: string, adjusted: number[], subscales: any[]): number {
		if (method === 'subscale_average' && subscales.length) {
			const sum = subscales.reduce((total, subscale) => total + Number(subscale.score || 0), 0);
			return sum / subscales.length;
		}
		return adjusted.reduce((total, value) => total + value, 0);
	}

	private findBand(bands: any, score: number): any | null {
		if (!Array.isArray(bands)) return null;
		return bands.find((band) => {
			const min = band?.min != null ? Number(band.min) : Number.NEGATIVE_INFINITY;
			const max = band?.max != null ? Number(band.max) : Number.POSITIVE_INFINITY;
			return score >= min && score <= max;
		}) || null;
	}

	private feedbackText(band: any): string {
		if (!band) return '';
		if (this.audience === 'therapist') {
			return this.localizedText(band, 'therapistText') || this.localizedText(band, 'clientText');
		}
		return this.localizedText(band, 'clientText') || this.localizedText(band, 'therapistText');
	}

	private computeRisk(riskRules: any, items: any[], answers: any[]): any {
		const itemId = riskRules?.itemId || null;
		if (!itemId) return { triggered: false, message: '', itemId: null };
		const index = items.findIndex((item) => item?.id === itemId);
		const value = index >= 0 ? Number(answers[index]) : NaN;
		const threshold = Number(riskRules?.threshold ?? 1);
		const triggered = isFinite(value) && value >= threshold;
		return {
			triggered,
			message: triggered ? this.riskMessage(riskRules) : '',
			itemId
		};
	}

	private riskMessage(riskRules: any): string {
		if (this.audience === 'therapist') {
			return this.localizedText(riskRules, 'message') || this.tr('Toolkit.Survey.Results.Risk.TherapistFallback');
		}
		return this.tr('Toolkit.Survey.Results.Risk.Client.Body');
	}

	private responseLabel(instrument: any, value: any): string {
		const scale = Array.isArray(instrument?.responseScale) ? instrument.responseScale : [];
		const option = scale.find((entry) => Number(entry?.value) === Number(value));
		return this.localizedText(option, 'label');
	}

	private localizedBand(band: any): any | null {
		if (!band) return null;
		const rawLabel = (band?.label || band?.severity || '').toString();
		const localizedLabel = this.localizedText(band, 'label');
		const rawClientText = (band?.clientText || '').toString();
		const localizedClientText = this.localizedText(band, 'clientText');
		const rawTherapistText = (band?.therapistText || '').toString();
		const localizedTherapistText = this.localizedText(band, 'therapistText');
		return {
			...band,
			label: (localizedLabel && localizedLabel !== rawLabel)
				? localizedLabel
				: this.bandTranslation(rawLabel) || localizedLabel || rawLabel,
			clientText: (localizedClientText && localizedClientText !== rawClientText)
				? localizedClientText
				: this.bandTextTranslation(rawLabel, 'Client') || this.bandTextTranslation(rawClientText, 'Client') || localizedClientText || rawClientText,
			therapistText: (localizedTherapistText && localizedTherapistText !== rawTherapistText)
				? localizedTherapistText
				: this.bandTextTranslation(rawLabel, 'Therapist') || this.bandTextTranslation(rawTherapistText, 'Therapist') || localizedTherapistText || rawTherapistText
		};
	}

	private localize(value: any): any {
		return localizeToolkitContent(value, this.currentLocale());
	}

	private localizedText(source: any, baseKey: string): string {
		if (!source || typeof source !== 'object') return '';
		const localized = this.localize(source);
		const values = [
			localized?.[baseKey],
			this.localizedTranslationText(source, baseKey),
			...this.localizedKeyVariants(baseKey).map((key) => source?.[key]),
			source?.[baseKey]
		];
		for (const value of values) {
			if (value !== undefined && value !== null && value !== '') return value.toString().trim();
		}
		return '';
	}

	private localizedTranslationText(source: any, baseKey: string): string {
		const variants = this.localeVariants();
		const translation = source?.translation;
		if (translation && typeof translation === 'object') {
			const language = this.normalizeLocale(translation?.language);
			if (!language || variants.includes(language)) {
				const value = this.translationValue(translation, baseKey);
				if (value) return value;
			}
		}
		if (!Array.isArray(source?.translations)) return '';
		for (const variant of variants) {
			const match = source.translations.find((entry: any) => this.normalizeLocale(entry?.language) === variant);
			const value = this.translationValue(match, baseKey);
			if (value) return value;
		}
		return '';
	}

	private translationValue(translation: any, baseKey: string): string {
		if (!translation || typeof translation !== 'object') return '';
		return (
			translation?.[baseKey] ??
			translation?.translationText ??
			translation?.text ??
			translation?.label ??
			translation?.title ??
			translation?.name ??
			''
		).toString().trim();
	}

	private localizedKeyVariants(baseKey: string): string[] {
		const language = this.localeVariants()[1] || this.localeVariants()[0] || '';
		if (!language) return [];
		const capitalized = language.charAt(0).toUpperCase() + language.slice(1);
		return [
			`${baseKey}${language.toUpperCase()}`,
			`${baseKey}${capitalized}`,
			`${baseKey}_${language}`
		];
	}

	private localeVariants(): string[] {
		const locale = this.normalizeLocale(this.currentLocale());
		if (!locale) return [];
		const language = locale.split('_')[0];
		return Array.from(new Set([locale, language].filter((value) => !!value)));
	}

	private normalizeLocale(locale: any): string {
		return (locale || '').toString().trim().replace(/-/g, '_').toLowerCase();
	}

	private formatScore(value: number): string {
		if (!isFinite(value)) return '0';
		return Number.isInteger(value) ? value.toString() : value.toFixed(1);
	}

	private formatDate(value: any): string {
		if (!value) return '';
		const date = new Date(value);
		if (!isFinite(date.getTime())) return '';
		return new Intl.DateTimeFormat(this.locale(), { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
	}

	private copy(): any {
		return {
			clientTitle: this.tr('Toolkit.Survey.Results.Client.Title'),
			clientBody: this.tr('Toolkit.Survey.Results.Client.Body'),
			therapistTitle: this.tr('Toolkit.Survey.Results.Therapist.Title'),
			therapistBody: this.tr('Toolkit.Survey.Results.Therapist.Body'),
			riskTitle: this.tr('Toolkit.Survey.Results.Risk.Title'),
			completed: this.tr('Toolkit.Survey.Results.Completed'),
			score: this.tr('Toolkit.Survey.Results.Score'),
			subscaleScores: this.tr('Toolkit.Survey.Results.SubscaleScores'),
			responses: this.tr('Toolkit.Survey.Results.Responses'),
			item: this.tr('Toolkit.Survey.Results.Table.Item'),
			response: this.tr('Toolkit.Survey.Results.Table.Response'),
			reverseScored: this.tr('Toolkit.Survey.Results.ReverseScored')
		};
	}

	private tr(key: string): string {
		const translated = this.dictionaryService.translate(key);
		return translated && translated !== key ? translated : key;
	}

	private bandTranslation(label: string): string {
		const suffix = this.dictionaryKeySuffix(label);
		if (!suffix) return '';
		const key = `Toolkit.Survey.Results.Band.${suffix}`;
		const translated = this.dictionaryService.translate(key);
		return translated && translated !== key ? translated : '';
	}

	private bandTextTranslation(label: string, audience: 'Client' | 'Therapist'): string {
		const suffix = this.dictionaryKeySuffix(label);
		if (!suffix) return '';
		const key = `Toolkit.Survey.Results.BandText.${suffix}.${audience}`;
		const translated = this.dictionaryService.translate(key);
		return translated && translated !== key ? translated : '';
	}

	private dictionaryKeySuffix(value: string): string {
		return (value || '')
			.toString()
			.trim()
			.replace(/[^A-Za-z0-9]+(.)/g, (_match, chr) => chr.toUpperCase())
			.replace(/^[a-z]/, (chr) => chr.toUpperCase());
	}

	private locale(): string {
		const current = this.currentLocale().replace('_', '-');
		return current || 'en-GB';
	}

	private currentLocale(): string {
		return (this.dictionaryService.getCurrentLocale() || 'en_GB').toString();
	}
}
